import { captureNode, waitForImagesInNode } from '../capture-image-utils'

const swapWaveSvgForCapture = node => {
  const gradient = node.querySelector('#googleWaveGradient')
  const svg = gradient?.closest('svg')
  if (!svg) return () => {}

  const bounds = svg.getBoundingClientRect()
  const markup = new XMLSerializer().serializeToString(svg)
  const image = document.createElement('img')
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
  image.width = Math.round(bounds.width)
  image.height = Math.round(bounds.height)
  image.style.display = 'block'
  image.style.width = `${bounds.width}px`
  image.style.height = `${bounds.height}px`
  image.alt = ''
  image.dataset.googleWaveCapture = 'true'
  svg.replaceWith(image)

  return () => image.replaceWith(svg)
}

const hidePassBack = (pass, frontPreview) => {
  if (!pass || !frontPreview) return () => {}

  const backElements = Array.from(pass.children)
    .filter(child => child !== frontPreview && !child.contains(frontPreview))
  const originalDisplays = backElements.map(element => element.style.display)

  backElements.forEach(element => {
    element.style.display = 'none'
  })

  return () => {
    backElements.forEach((element, index) => {
      element.style.display = originalDisplays[index]
    })
  }
}

const expandCaptureBounds = node => {
  const originalHeight = node.style.height
  const originalOverflow = node.style.overflow
  const nodeBounds = node.getBoundingClientRect()
  const descendants = Array.from(node.querySelectorAll('*'))
  const bottom = descendants.reduce((maxBottom, element) => {
    return Math.max(maxBottom, element.getBoundingClientRect().bottom)
  }, nodeBounds.bottom)
  const requiredHeight = Math.ceil(bottom - nodeBounds.top)

  if (requiredHeight > nodeBounds.height) {
    node.style.height = `${requiredHeight}px`
    node.style.overflow = 'visible'
  }

  return () => {
    node.style.height = originalHeight
    node.style.overflow = originalOverflow
  }
}

const cropCaptureBottomToWave = node => {
  const wave = node.querySelector('img[data-google-wave-capture="true"]')
  const heroImage = node.querySelector('.heroImage')
  const contentBottom = wave?.getBoundingClientRect().bottom ?? heroImage?.getBoundingClientRect().bottom
  if (contentBottom === undefined) return () => {}

  const originalHeight = node.style.height
  const nodeTop = node.getBoundingClientRect().top
  node.style.height = `${Math.ceil(contentBottom - nodeTop)}px`

  return () => {
    node.style.height = originalHeight
  }
}

const clipCaptureToRoundedPass = node => {
  const originalOverflow = node.style.overflow
  const originalBottomLeftRadius = node.style.borderBottomLeftRadius
  const originalBottomRightRadius = node.style.borderBottomRightRadius
  const computed = window.getComputedStyle(node)

  node.style.overflow = 'hidden'
  node.style.borderBottomLeftRadius = computed.borderBottomLeftRadius
  node.style.borderBottomRightRadius = computed.borderBottomRightRadius

  return () => {
    node.style.overflow = originalOverflow
    node.style.borderBottomLeftRadius = originalBottomLeftRadius
    node.style.borderBottomRightRadius = originalBottomRightRadius
  }
}

const captureGoogleFrontPreview = async () => {
  const frontPreview = document.getElementById('googleFrontPreview')
  const card = document.getElementById('googleFront')
  const pass = frontPreview?.closest('.pass') ?? card?.closest('.pass')
  const node = pass ?? frontPreview ?? card
  if (!node) throw new Error('Google front preview node not found')

  node.classList.add("preview")
  const restorePassBack = hidePassBack(pass, frontPreview)
  const restoreWaveSvg = swapWaveSvgForCapture(node)
  const restoreCaptureBounds = expandCaptureBounds(node)
  const restoreWaveCrop = cropCaptureBottomToWave(node)
  const restoreRoundedClip = clipCaptureToRoundedPass(node)

  try {
    await waitForImagesInNode(node)
    return await captureNode(node)
  }
  finally {
    restoreRoundedClip()
    restoreWaveCrop()
    restoreCaptureBounds()
    restoreWaveSvg()
    restorePassBack()
    node.classList.remove("preview")
  }
}

export default captureGoogleFrontPreview
